import { useState, useEffect } from 'react';
import { ViewButton } from './style';
import { Modal, View, Text, Dimensions, ScrollView } from 'react-native';
import FlatListMultipleSelection from '../../ClickCard/FlatListMultipleSelection';
import { Calendar } from 'react-native-calendars';
import Button from '../../../components/Button';
import { SmallText, SubTitle } from '../../../config/theme/globalStyles';
import AdminInput from '../../../components/AdminInput';
import AdminAlert from '../../../components/AdminAlert';
import AdminCheckbox from '../../../components/AdminCheckbox';
import { ADMIN_COLORS, ADMIN_FONT } from '../../../config/theme/adminColors';
import { emailValidator, endDateValidator, selectedProductsValidator } from "../../../utils/validators";
import { HelperText } from "react-native-paper";

const screenWidth = Dimensions.get("window").width;
const windowHeight = Dimensions.get('window').height;

const CreateUserModal = ({ products, visible, onClose, onSave }) => {
  const [email, setEmail] = useState({ value: "", error: "" });
  const [name, setName] = useState({ value: "", error: "" });
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [endDate, setEndDate] = useState({ value: "", error: "" });
  const [loadingSave, setLoadingSave] = useState(false);
  const [productsError, setProductsError] = useState("");
  const [isSupportOnlyAdmin, setIsSupportOnlyAdmin] = useState(false);
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    if (visible) {
      setSelectedProducts(products.map(product => ({ ...product, selected: false })));
      setEmail({ value: "", error: "" });
      setName({ value: "", error: "" });
      setEndDate({ value: "", error: "" });
      setProductsError("");
      setIsSupportOnlyAdmin(false);
      setSaveError("");
    }
  }, [visible]);

  const handleDateSelect = (date) => {
    const today = new Date();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const selectedDate = new Date(date.timestamp);
    const offset = selectedDate.getTimezoneOffset();
    selectedDate.setMinutes(selectedDate.getMinutes() + offset);
    const selectedDateStart = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
    selectedDate.setHours(23, 59, 0, 0);

    if (selectedDateStart >= todayStart) {
      setEndDate({ value: selectedDate, error: "" });
    } else {
      setEndDate({ ...endDate, error: "Data de término deve ser no futuro" });
    }
  };

  const validateInputs = () => {
    const emailError = emailValidator(email.value.trim());
    const endDateError = isSupportOnlyAdmin ? "" : endDateValidator(endDate.value);
    const selectedProductsError = isSupportOnlyAdmin
      ? ""
      : selectedProductsValidator(selectedProducts);

    setEmail({ ...email, error: emailError });
    setName({ ...name, error: name.value ? "" : "Nome é obrigatório" });
    setEndDate({ ...endDate, error: endDateError });
    setProductsError(selectedProductsError);

    return !emailError && !endDateError && !selectedProductsError;
  };

  const renderArrow = (direction) => (
    <View>
      <Text>{direction === 'left' ? '<' : '>'}</Text>
    </View>
  );

  const handleSave = async () => {
    setLoadingSave(true);
    setSaveError("");

    if (validateInputs()) {
      try {
        await onSave({
          // Normalizado na origem: casing/espaço divergente criava aluno
          // "invisível" para a busca exata do painel.
          email: email.value.trim().toLowerCase(),
          selectedProducts,
          endDate: endDate.value,
          name: name.value,
          isSupportOnlyAdmin,
        });
        onClose();
      } catch (error) {
        console.error('Erro ao salvar usuário:', error);
        setSaveError('Não foi possível salvar o aluno. Tente novamente.');
      } finally {
        setLoadingSave(false);
      }
    } else {
      setLoadingSave(false);
    }
  };

  return (
    <Modal
      animationType="fade"
      visible={visible}
      onRequestClose={onClose}
      transparent={true}
    >
      <View
        style={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
        }}
      >
        <View
          style={{
            maxWidth: screenWidth > 980 ? screenWidth * 0.6 : screenWidth * 0.9,
            backgroundColor: 'white',
            maxHeight: windowHeight * 0.8,
            borderRadius: 10,
            padding: 5,
            alignItems: 'center',
          }}
        >
          <ScrollView
            contentContainerStyle={{ paddingHorizontal: '1.5rem', flexGrow: 1 }}
            showsVerticalScrollIndicator={true}
            style={{ width: '100%', height: '100%' }}
          >
            <SubTitle padding="1rem 0rem" color={ADMIN_COLORS.ink} fontFamily={ADMIN_FONT}>
              Adicionar novo(a) aluno(a) manualmente
            </SubTitle>

            <Text style={{ fontFamily: ADMIN_FONT, fontSize: 14, fontWeight: '600', color: ADMIN_COLORS.label }}>
              Email
            </Text>
            <AdminInput
              value={email.value}
              onChangeText={(text) => setEmail({ value: text, error: '' })}
              placeholder="Email do aluno"
              autoCapitalize="none"
              autoCompleteType="email"
              textContentType="emailAddress"
              keyboardType="email-address"
            />
            <HelperText type="error" visible={!!email.error}>
              {email.error}
            </HelperText>

            <Text style={{ fontFamily: ADMIN_FONT, fontSize: 14, fontWeight: '600', color: ADMIN_COLORS.label }}>
              Nome completo
            </Text>
            <AdminInput
              value={name.value}
              onChangeText={(text) => setName({ value: text, error: '' })}
              placeholder="Digite o nome completo"
            />
            <HelperText type="error" visible={!!name.error}>
              {name.error}
            </HelperText>

            <AdminCheckbox
              value={isSupportOnlyAdmin}
              onValueChange={setIsSupportOnlyAdmin}
              style={{ width: "100%", alignItems: "flex-start", marginTop: 4, marginBottom: 12 }}
            >
              <Text style={{ flex: 1, fontFamily: ADMIN_FONT, fontSize: 14, lineHeight: 20, color: ADMIN_COLORS.label }}>
                Cadastrar usuário como administrador de Suporte (acesso a Notificações, Comunidade e Comentários)
              </Text>
            </AdminCheckbox>

            {!isSupportOnlyAdmin && (
              <>
                <FlatListMultipleSelection
                  items={selectedProducts}
                  setItems={setSelectedProducts}
                  smallText="Produtos Selecionados:"
                  footerText="Selecione os produtos:"
                />
                {productsError ? (
                  <HelperText type="error">{productsError}</HelperText>
                ) : null}
                <SmallText color={ADMIN_COLORS.label} fontFamily={ADMIN_FONT}>
                  Data de fim de acesso:
                </SmallText>
                <Calendar
                  onDayPress={handleDateSelect}
                  markedDates={
                    endDate.value
                      ? {
                        [`${endDate.value.getFullYear()}-${String(endDate.value.getMonth() + 1).padStart(2, '0')}-${String(endDate.value.getDate()).padStart(2, '0')}`]: {
                          selected: true,
                          selectedColor: '#2B93ED',
                        },
                      }
                      : {}
                  }
                  renderArrow={renderArrow}
                />
                <HelperText type="error" visible={!!endDate.error}>
                  {endDate.error}
                </HelperText>
              </>
            )}
            <SmallText color={ADMIN_COLORS.muted} fontFamily={ADMIN_FONT}>A senha padrão gerada é: app@1234</SmallText>

            {saveError ? (
              <AdminAlert variant="error" onClose={() => setSaveError("")}>
                {saveError}
              </AdminAlert>
            ) : null}

            <ViewButton style={{ marginTop: '1rem' }}>
              <Button
                title="Cancelar"
                onPress={onClose}
                margin="0rem 1rem 0rem 0rem"
                colorbutton={ADMIN_COLORS.secondaryBg}
                colortitle={ADMIN_COLORS.secondaryText}
              />
              <Button
                title="Salvar"
                isLoading={loadingSave}
                onPress={handleSave}
                colorbutton={ADMIN_COLORS.primaryBg}
                colortitle={'#ffffff'}
              />
            </ViewButton>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

export default CreateUserModal;
